import { useEffect, useRef, useCallback } from 'react'
import { BACKEND_URL } from '../lib/api'

export type WsMessage = {
  event: string
  data: Record<string, unknown>
}

type Handler = (msg: WsMessage) => void

/**
 * Connects to the MENO WebSocket hub and fires `onMessage` for every event.
 * Automatically reconnects on disconnect with exponential back-off.
 */
export function useWebSocket(onMessage: Handler) {
  const wsRef      = useRef<WebSocket | null>(null)
  const retryRef   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const delayRef   = useRef(1000)
  const handlerRef = useRef<Handler>(onMessage)

  useEffect(() => {
    handlerRef.current = onMessage
  }, [onMessage])

  const connect = useCallback(() => {
    let wsUrl: string
    if (BACKEND_URL.startsWith('http')) {
      const protocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws'
      wsUrl = `${protocol}://${BACKEND_URL.replace(/^https?:\/\//, '')}/ws`
    } else {
      // Fallback to relative if BACKEND_URL is not set (for local dev)
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
      wsUrl = `${protocol}://${window.location.host}/ws`
    }

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      delayRef.current = 1000 // reset back-off
    }

    ws.onmessage = (evt) => {
      try {
        const msg: WsMessage = JSON.parse(evt.data)
        handlerRef.current(msg)
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      retryRef.current = setTimeout(() => {
        delayRef.current = Math.min(delayRef.current * 2, 30000)
        connect()
      }, delayRef.current)
    }

    ws.onerror = () => ws.close()
  }, [])

  useEffect(() => {
    connect()
    return () => {
      retryRef.current && clearTimeout(retryRef.current)
      wsRef.current?.close()
    }
  }, [connect])

  const send = useCallback((event: string, data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ event, data }))
    }
  }, [])

  return { send }
}
